import React, { useState, useEffect } from 'react'
import { api } from '../../services/authService'
import { formatCurrency } from '../../utils/formatters'

const statusLabel = {
  PENDING: { text: 'Pendente', cls: 'bg-yellow-100 text-yellow-800 dark:bg-yellow-900/30 dark:text-yellow-400' },
  OVERDUE: { text: 'Vencida', cls: 'bg-red-100 text-red-800 dark:bg-red-900/30 dark:text-red-400' },
  PAID:    { text: 'Paga',    cls: 'bg-green-100 text-green-800 dark:bg-green-900/30 dark:text-green-400' },
  CANCELLED: { text: 'Cancelada', cls: 'bg-gray-100 text-gray-600 dark:bg-gray-700 dark:text-gray-400' },
}

function isFutureInvoice(due_date) {
  if (!due_date) return false
  const now = new Date()
  const endOfCurrentMonth = new Date(now.getFullYear(), now.getMonth() + 1, 0)
  return new Date(due_date) > endOfCurrentMonth
}

function formatDate(dateStr) {
  if (!dateStr) return '—'
  const d = new Date(dateStr)
  return d.toLocaleDateString('pt-BR')
}

const CancelContractModal = ({ contract, onClose, onConfirm }) => {
  const [invoices, setInvoices] = useState([])
  const [loading, setLoading] = useState(true)
  const [selected, setSelected] = useState({})
  const [confirming, setConfirming] = useState(false)

  useEffect(() => {
    const load = async () => {
      try {
        const res = await api.get('/api/invoices', { params: { contractId: contract.id } })
        const list = res.data?.data || []
        // Ordenar por vencimento
        list.sort((a, b) => (a.due_date || '').localeCompare(b.due_date || ''))
        setInvoices(list)

        // Pré-selecionar: futuras não pagas
        const initial = {}
        for (const inv of list) {
          if (inv.status !== 'PAID' && inv.status !== 'CANCELLED') {
            initial[inv.id] = isFutureInvoice(inv.due_date)
          }
        }
        setSelected(initial)
      } catch (e) {
        console.error('Erro ao carregar faturas:', e)
      } finally {
        setLoading(false)
      }
    }
    load()
  }, [contract.id])

  const cancellable = invoices.filter(inv => inv.status !== 'PAID' && inv.status !== 'CANCELLED')
  const selectedIds = cancellable.filter(inv => selected[inv.id]).map(inv => inv.id)
  const selectedWithCharge = cancellable.filter(inv => selected[inv.id] && inv.asaas_charge_id)
  const totalSelectedValue = cancellable
    .filter(inv => selected[inv.id])
    .reduce((sum, inv) => sum + parseFloat(inv.total_value || 0), 0)

  const toggleAll = (val) => {
    const next = {}
    for (const inv of cancellable) next[inv.id] = val
    setSelected(next)
  }

  const allChecked = cancellable.length > 0 && cancellable.every(inv => selected[inv.id])
  const noneChecked = cancellable.every(inv => !selected[inv.id])

  const handleConfirm = async () => {
    setConfirming(true)
    try {
      await onConfirm(contract, selectedIds)
    } finally {
      setConfirming(false)
    }
  }

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/50 backdrop-blur-sm p-4">
      <div className="bg-white dark:bg-gray-900 rounded-2xl shadow-2xl w-full max-w-2xl max-h-[90vh] flex flex-col">
        {/* Header */}
        <div className="px-6 py-5 border-b border-gray-200 dark:border-gray-700 flex items-start justify-between">
          <div>
            <h2 className="text-lg font-bold text-gray-900 dark:text-white">
              Cancelar Contrato
            </h2>
            <p className="text-sm text-gray-500 dark:text-gray-400 mt-0.5">
              {contract.client_name} — {contract.contract_identifier || contract.contract_number}
            </p>
          </div>
          <button
            onClick={onClose}
            className="text-gray-400 hover:text-gray-600 dark:hover:text-gray-200 text-xl leading-none p-1"
          >
            ✕
          </button>
        </div>

        {/* Info banner */}
        <div className="px-6 py-3 bg-amber-50 dark:bg-amber-900/20 border-b border-amber-200 dark:border-amber-800 text-sm text-amber-800 dark:text-amber-300">
          Selecione quais faturas devem ser canceladas. Faturas <strong>pagas</strong> não podem ser alteradas.
          Faturas <strong>vencidas</strong> e do <strong>mês atual</strong> são desmarcadas por padrão — ainda são dívida do cliente.
        </div>

        {/* Body */}
        <div className="flex-1 overflow-y-auto px-6 py-4">
          {loading ? (
            <div className="flex items-center justify-center py-10">
              <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-blue-600" />
            </div>
          ) : invoices.length === 0 ? (
            <div className="text-center py-10 text-gray-400">
              <p className="text-3xl mb-2">📭</p>
              <p>Nenhuma fatura encontrada para este contrato.</p>
              <p className="text-sm mt-1">O contrato será cancelado sem afetar cobranças.</p>
            </div>
          ) : (
            <>
              {/* Controles */}
              {cancellable.length > 0 && (
                <div className="flex items-center gap-3 mb-3">
                  <button
                    onClick={() => toggleAll(true)}
                    disabled={allChecked}
                    className="text-xs px-3 py-1.5 rounded-lg border border-gray-300 dark:border-gray-600 text-gray-600 dark:text-gray-300 hover:bg-gray-50 dark:hover:bg-gray-800 disabled:opacity-40 transition"
                  >
                    Selecionar todas
                  </button>
                  <button
                    onClick={() => toggleAll(false)}
                    disabled={noneChecked}
                    className="text-xs px-3 py-1.5 rounded-lg border border-gray-300 dark:border-gray-600 text-gray-600 dark:text-gray-300 hover:bg-gray-50 dark:hover:bg-gray-800 disabled:opacity-40 transition"
                  >
                    Desmarcar todas
                  </button>
                  <span className="text-xs text-gray-400 ml-auto">
                    {selectedIds.length} de {cancellable.length} selecionadas
                  </span>
                </div>
              )}

              {/* Lista de faturas */}
              <div className="space-y-2">
                {invoices.map(inv => {
                  const isPaid = inv.status === 'PAID'
                  const isCancelled = inv.status === 'CANCELLED'
                  const isLocked = isPaid || isCancelled
                  const future = isFutureInvoice(inv.due_date)
                  const st = statusLabel[inv.status] || statusLabel.PENDING

                  return (
                    <label
                      key={inv.id}
                      className={`flex items-center gap-3 p-3 rounded-xl border transition ${
                        isLocked
                          ? 'border-gray-100 dark:border-gray-800 bg-gray-50 dark:bg-gray-800/50 opacity-60 cursor-not-allowed'
                          : selected[inv.id]
                          ? 'border-red-300 dark:border-red-700 bg-red-50 dark:bg-red-900/20 cursor-pointer'
                          : 'border-gray-200 dark:border-gray-700 bg-white dark:bg-gray-800 hover:border-gray-300 dark:hover:border-gray-600 cursor-pointer'
                      }`}
                    >
                      <input
                        type="checkbox"
                        checked={!!selected[inv.id]}
                        disabled={isLocked}
                        onChange={e => setSelected(prev => ({ ...prev, [inv.id]: e.target.checked }))}
                        className="w-4 h-4 rounded text-red-600 border-gray-300 focus:ring-red-500 flex-shrink-0 disabled:opacity-50"
                      />
                      <div className="flex-1 min-w-0">
                        <div className="flex items-center gap-2 flex-wrap">
                          <span className="text-sm font-semibold text-gray-900 dark:text-white">
                            #{inv.invoice_number}
                          </span>
                          <span className={`text-xs px-2 py-0.5 rounded-full font-medium ${st.cls}`}>
                            {st.text}
                          </span>
                          {inv.asaas_charge_id && (
                            <span className="text-xs px-2 py-0.5 rounded-full bg-blue-100 text-blue-700 dark:bg-blue-900/30 dark:text-blue-400 font-medium">
                              Boleto Asaas
                            </span>
                          )}
                          {!future && !isLocked && (
                            <span className="text-xs px-2 py-0.5 rounded-full bg-orange-100 text-orange-700 dark:bg-orange-900/30 dark:text-orange-400">
                              Mês atual / vencida
                            </span>
                          )}
                        </div>
                        <div className="text-xs text-gray-500 dark:text-gray-400 mt-0.5">
                          Vencimento: {formatDate(inv.due_date)}
                          {inv.period_start && inv.period_end && (
                            <span className="ml-2">· Período: {formatDate(inv.period_start)} – {formatDate(inv.period_end)}</span>
                          )}
                        </div>
                      </div>
                      <div className="text-sm font-bold text-gray-900 dark:text-white flex-shrink-0">
                        {formatCurrency(inv.total_value)}
                      </div>
                    </label>
                  )
                })}
              </div>
            </>
          )}
        </div>

        {/* Footer com resumo */}
        <div className="px-6 py-4 border-t border-gray-200 dark:border-gray-700 bg-gray-50 dark:bg-gray-800/50 rounded-b-2xl">
          {selectedIds.length > 0 && (
            <div className="mb-3 p-3 rounded-lg bg-red-50 dark:bg-red-900/20 border border-red-200 dark:border-red-800 text-sm text-red-800 dark:text-red-300 space-y-1">
              <p>
                <strong>{selectedIds.length}</strong> fatura(s) serão canceladas
                {selectedWithCharge.length > 0 && (
                  <> — <strong>{selectedWithCharge.length}</strong> boleto(s) serão cancelados no Asaas</>
                )}
              </p>
              <p>Total que deixará de ser cobrado: <strong>{formatCurrency(totalSelectedValue)}</strong></p>
            </div>
          )}

          <div className="flex justify-end gap-3">
            <button
              onClick={onClose}
              disabled={confirming}
              className="px-5 py-2 rounded-lg border border-gray-300 dark:border-gray-600 text-gray-700 dark:text-gray-300 hover:bg-gray-100 dark:hover:bg-gray-700 text-sm font-medium transition disabled:opacity-50"
            >
              Voltar
            </button>
            <button
              onClick={handleConfirm}
              disabled={confirming}
              className="px-5 py-2 rounded-lg bg-red-600 text-white hover:bg-red-700 text-sm font-semibold transition disabled:opacity-70 flex items-center gap-2"
            >
              {confirming ? (
                <>
                  <div className="animate-spin rounded-full h-4 w-4 border-b-2 border-white" />
                  Cancelando...
                </>
              ) : (
                `Confirmar Cancelamento`
              )}
            </button>
          </div>
        </div>
      </div>
    </div>
  )
}

export default CancelContractModal
